import { Injectable } from '@angular/core';
import { ComponentType } from '@angular/cdk/portal';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable, map } from 'rxjs';
import moment from 'moment';
import { RestClientService } from '@core/service/rest-client.service';
import { AuthService } from '@core/service/auth.service';
import { PrintService } from '@core/service/print.service';
import { UtilitiesService } from '@core/service/utilities.service';
import { ModalSortieClientComponent } from 'app/accueil/hebergement/modal-sortie-client/modal-sortie-client.component';
import { ModalReglementHebergementComponent } from 'app/accueil/hebergement/modal-reglement-hebergement/modal-reglement-hebergement.component';
import { ModalDemandeRemiseHebergementComponent } from 'app/accueil/hebergement/modal-demande-remise-hebergement/modal-demande-remise-hebergement.component';
import { ModalProlongationPassComponent } from 'app/accueil/hebergement/modal-prolongation-pass/modal-prolongation-pass.component';
import { ModalProlongationSejourComponent } from 'app/accueil/hebergement/modal-prolongation-sejour/modal-prolongation-sejour.component';
import { ModalEtatDesLieuxComponent } from 'app/accueil/hebergement/modal-etat-des-lieux/modal-etat-des-lieux.component';
import { ModalCommadeRestauComponent } from 'app/restaurant/commande-restaurant/modal-commade-restau/modal-commade-restau.component';
import { ModalCommandeBoutiqueComponent } from 'app/boutique/commande-boutique/modal-commande-boutique/modal-commande-boutique.component';
import { ModalReglementReservationComponent } from 'app/accueil/reservation/modal-reglement-reservation/modal-reglement-reservation.component';
import { ModalVerificationReglementReservationComponent } from 'app/accueil/reservation/modal-verification-reglement-reservation/modal-verification-reglement-reservation.component';
import { ModalReglementOccupationComponent } from 'app/location-espace/occupation-espace/modal-reglement-occupation/modal-reglement-occupation.component';
import { ModalRemiseOccupationComponent } from 'app/location-espace/occupation-espace/modal-remise-occupation/modal-remise-occupation.component';
import { ModalFinOccupationComponent } from 'app/location-espace/occupation-espace/modal-fin-occupation/modal-fin-occupation.component';
import { FormReglementReservationEspaceComponent } from 'app/location-espace/reservation-espace/form-reglement-reservation-espace/form-reglement-reservation-espace.component';
import { FormDemandeRemiseReservationEspaceComponent } from 'app/location-espace/reservation-espace/form-demande-remise-reservation-espace/form-demande-remise-reservation-espace.component';
import { FormAnnulerReservationEspaceComponent } from 'app/location-espace/reservation-espace/form-annuler-reservation-espace/form-annuler-reservation-espace.component';

/** Action proposée sur un séjour ; `executer` ouvre la modale et rend la main à sa fermeture. */
export interface ActionSejour {
  libelle: string;
  icone: string;
  danger?: boolean;
  executer: () => Promise<void>;
}

const MODALE_LARGE: NgbModalOptions = { size: 'xl', backdrop: 'static', keyboard: false, centered: true };

/**
 * Actions sur un hébergement ou une réservation, hors des écrans de caisse : mêmes modales
 * et mêmes conditions que la liste des hébergements en cours et la liste des réservations.
 */
@Injectable({ providedIn: 'root' })
export class SejourActionsService {
  constructor(
    private modalService: NgbModal,
    private restClient: RestClientService,
    private authService: AuthService,
    private printService: PrintService,
    private utilities: UtilitiesService
  ) {}

  chargerHebergement(id: number, type: 'CHAMBRE' | 'ESPACE' = 'CHAMBRE'): Observable<any | null> {
    const user = this.authService.getCurrentUser();
    return this.restClient
      .post('hebergement/getByCriteria', {
        user_id: user?.id,
        type_hebergement_codification: type,
        is_for_accueil: type === 'CHAMBRE' ? true : undefined,
        data: { id },
        index: 0,
        size: 1,
      })
      .pipe(map((res: any) => (!res?.hasError && res?.items?.[0]) || null));
  }

  chargerReservation(id: number, type: 'CHAMBRE' | 'ESPACE' = 'CHAMBRE'): Observable<any | null> {
    const user = this.authService.getCurrentUser();
    return this.restClient
      .post('reservation/getByCriteria', {
        user_id: user?.id,
        type_reservation_codification: type,
        data: { id },
        index: 0,
        size: 1,
      })
      .pipe(map((res: any) => (!res?.hasError && res?.items?.[0]) || null));
  }

  actionsHebergement(item: any, codificationService?: string): ActionSejour[] {
    const expire = item.date_fin && moment().isAfter(moment(item.date_fin));
    const enCours = !expire && !item.is_sortie;
    const sortiePossible =
      !['HEBERGEMENT', 'ANNULE', 'SORTIE'].includes(item.statut_code) && !item.is_sortie;
    const formule = codificationService || item.service_codification;
    const actions: ActionSejour[] = [
      { libelle: 'Règlements', icone: 'payments', executer: () => this.ouvrir(ModalReglementHebergementComponent, (m) => (m.componentInstance.data = item)) },
    ];
    if (enCours && (formule === 'PASS' || formule === 'SEJOUR')) {
      actions.push({
        libelle: 'Prolonger',
        icone: 'more_time',
        executer: () =>
          this.ouvrir(
            (formule === 'PASS' ? ModalProlongationPassComponent : ModalProlongationSejourComponent) as ComponentType<any>,
            (m) => (m.componentInstance.data = { item, action: 'edit', type_service_codification: formule })
          ),
      });
    }
    if (enCours) {
      actions.push({ libelle: 'Demander une remise', icone: 'percent', executer: () => this.ouvrir(ModalDemandeRemiseHebergementComponent, (m) => (m.componentInstance.data = item)) });
      if (this.authService.hasRestaurant()) {
        actions.push({ libelle: 'Commande restaurant', icone: 'restaurant', executer: () => this.ouvrir(ModalCommadeRestauComponent, (m) => (m.componentInstance.data = item)) });
      }
      if (this.authService.hasBoutique()) {
        actions.push({ libelle: 'Commande boutique', icone: 'storefront', executer: () => this.ouvrir(ModalCommandeBoutiqueComponent, (m) => (m.componentInstance.data = item)) });
      }
    }
    actions.push(
      {
        libelle: 'État des lieux',
        icone: 'fact_check',
        executer: () =>
          this.ouvrir(ModalEtatDesLieuxComponent, (m) => {
            m.componentInstance.action = 'edit';
            m.componentInstance.itemToSave = { ...item };
          }),
      },
      { libelle: 'Imprimer la facture', icone: 'print', executer: () => this.imprimerFacture(item.id) }
    );
    if (sortiePossible) {
      actions.push({
        libelle: 'Enregistrer la sortie',
        icone: 'logout',
        danger: true,
        executer: () =>
          this.ouvrir(ModalSortieClientComponent, (m) => {
            m.componentInstance.action = 'edit';
            m.componentInstance.itemData = item;
          }, { size: 'xl', backdrop: 'static', keyboard: false }),
      });
    }
    return actions;
  }

  actionsReservation(item: any): ActionSejour[] {
    const actions: ActionSejour[] = [
      { libelle: 'Règlements', icone: 'payments', executer: () => this.ouvrir(ModalReglementReservationComponent, (m) => (m.componentInstance.data = item)) },
    ];
    if (item.has_pending_reglement_client) {
      actions.push({
        libelle: 'Vérifier le règlement du client',
        icone: 'verified',
        executer: () =>
          this.ouvrir(ModalVerificationReglementReservationComponent, (m) => (m.componentInstance.data = item), {
            size: 'lg', backdrop: 'static', keyboard: false,
          }),
      });
    }
    return actions;
  }

  /** Occupation d'espace : mêmes actions et conditions que l'écran « Occupation espace ». */
  actionsOccupation(item: any): ActionSejour[] {
    const actions: ActionSejour[] = [
      { libelle: 'Règlements', icone: 'payments', executer: () => this.ouvrir(ModalReglementOccupationComponent, (m) => (m.componentInstance.data = item)) },
      { libelle: 'Demander une remise', icone: 'percent', executer: () => this.ouvrir(ModalRemiseOccupationComponent, (m) => (m.componentInstance.data = item)) },
    ];
    if (!item.is_sortie && !['HEBERGEMENT', 'ANNULE', 'SORTIE'].includes(item.statut_code)) {
      actions.push({
        libelle: "Terminer l'occupation",
        icone: 'logout',
        danger: true,
        executer: () => this.ouvrir(ModalFinOccupationComponent, (m) => (m.componentInstance.data = { item, action: 'edit' })),
      });
    }
    return actions;
  }

  /** Réservation d'espace : mêmes actions et conditions que l'écran « Réservation espace ». */
  actionsReservationEspace(item: any): ActionSejour[] {
    const actions: ActionSejour[] = [
      { libelle: 'Règlements', icone: 'payments', executer: () => this.ouvrir(FormReglementReservationEspaceComponent, (m) => (m.componentInstance.data = item)) },
      { libelle: 'Demander une remise', icone: 'percent', executer: () => this.ouvrir(FormDemandeRemiseReservationEspaceComponent, (m) => (m.componentInstance.data = item)) },
    ];
    if (item.has_pending_reglement_client) {
      actions.push({
        libelle: 'Vérifier le règlement du client',
        icone: 'verified',
        executer: () =>
          this.ouvrir(ModalVerificationReglementReservationComponent, (m) => (m.componentInstance.data = item), {
            size: 'lg', backdrop: 'static', keyboard: false,
          }),
      });
    }
    if (!item.is_bloquee && !['HEBERGEMENT', 'ANNULE', 'SORTIE'].includes(item.statut_code)) {
      actions.push({
        libelle: 'Annuler la réservation',
        icone: 'event_busy',
        danger: true,
        executer: () => this.ouvrir(FormAnnulerReservationEspaceComponent, (m) => (m.componentInstance.data = { item, action: 'edit' })),
      });
    }
    return actions;
  }

  /**
   * Raison pour laquelle une réservation ne peut pas encore devenir un hébergement,
   * ou null si elle le peut. Mêmes règles que l'API (can_be_hebergement) et que la
   * modale de conversion.
   */
  motifEntreeImpossible(reservation: any): string | null {
    if (['HEBERGEMENT', 'SORTIE'].includes(reservation.statut_code)) return 'Le client est déjà entré.';
    if (reservation.statut_code === 'ANNULE') return 'Réservation annulée.';
    if (!reservation.is_validated) return 'Réservation non validée.';
    if (reservation.is_bloquee) return reservation.message_blocage || 'Réservation bloquée.';
    if (reservation.has_hebergement_expired) return 'La période de la réservation est dépassée.';
    if (!reservation.can_be_hebergement) {
      const arrivee = reservation.debut_reservation ? moment(reservation.debut_reservation) : null;
      return arrivee?.isValid()
        ? `Arrivée possible à partir du ${arrivee.format('DD/MM')} à ${arrivee.format('HH:mm')}.`
        : "Hors de la période d'arrivée.";
    }
    return null;
  }

  /** Résout à la fermeture de la modale, qu'elle soit validée ou abandonnée. */
  private ouvrir(
    composant: ComponentType<any>,
    preparer: (modalRef: NgbModalRef) => void,
    options: NgbModalOptions = MODALE_LARGE
  ): Promise<void> {
    const modalRef = this.modalService.open(composant, options);
    preparer(modalRef);
    return modalRef.result.then(() => undefined, () => undefined);
  }

  private imprimerFacture(id: number): Promise<void> {
    return new Promise((resolve) => {
      this.restClient.post('pdf/printFactureHebergement', { data: { id }, index: 0, size: 10000 }).subscribe({
        next: (res: any) => {
          if (res?.item && !res.hasError) {
            this.printService.previewPdf(res.item.link);
          } else {
            this.utilities.showNotification('snackbar-danger', this.utilities.formatMsgServeur(res?.status?.message), 'bottom', 'center');
          }
          resolve();
        },
        error: () => resolve(),
      });
    });
  }
}
